/**
 * Mutation hooks for list CRUD operations
 *
 * Provides React Query mutations wrapping the lists API endpoints.
 * Each mutation invalidates relevant query keys on success.
 */

import * as listsApi from '@/api/endpoints/lists';
import { LISTS_QUERY_KEYS } from '@/api/queries/lists';
import type { ListCreateInput, ListUpdateInput } from '@/api/endpoints/lists';
import { useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * Hook for creating a new custom list
 *
 * On success, invalidates lists collection so the list screen refreshes.
 */
export function useCreateList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ListCreateInput) => listsApi.createList(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.lists() });
    },
    onError: (error) => {
      console.error('[useCreateList] Failed to create list:', error);
    },
  });
}

/**
 * Hook for updating an existing list's name, description, or visibility
 *
 * On success, invalidates the specific list detail and the lists collection.
 */
export function useUpdateList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ListUpdateInput }) =>
      listsApi.updateList(id, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.list(variables.id) });
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.lists() });
    },
    onError: (error) => {
      console.error('[useUpdateList] Failed to update list:', error);
    },
  });
}

/**
 * Hook for deleting a custom list
 *
 * On success, invalidates the lists collection.
 */
export function useDeleteList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => listsApi.deleteList(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.lists() });
    },
    onError: (error) => {
      console.error('[useDeleteList] Failed to delete list:', error);
    },
  });
}
